import { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { usePageLoading } from "@/hooks/usePageLoading";
import { useRememberedCategory } from "@/hooks/useRememberedCategory";
import ButtonElement from "@/components/elements/Button/Button";
import CardWithImage from "@/components/elements/Card/CardThreeDImage";
import TitleWithContent from "@/components/elements/TitleWithContent/TitleWithContent";
import { products as localProducts } from "./ProductsArray";
import { fetchProductCards, fetchProductCategories } from "@/lib/queries";
import type { SanityImageData } from "@/types/sanity";
import getProductImage from "@/assets/images/products/getProductImages";
import { NavLink, useLocation } from "react-router-dom";

const cardItemVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

interface ProductsProps {
  showDropdown?: boolean;
}

interface Product {
  linkUrl: string;
  /** Bundled image URL (local products). */
  imageUrl?: string;
  /** Sanity image (CMS products). */
  image?: SanityImageData | null;
  cardTitle: string;
  cardContent: string;
  category: string;
  imageFileName?: string;
  slug?: string;
  subcategory?: string | null;
}

// Local products never change at runtime, so resolve their bundled images once.
const LOCAL_PRODUCTS: Product[] = localProducts.map((product) => ({
  ...product,
  imageUrl: getProductImage(product.imageFileName),
}));

// Subcategories of "Concrete Admixtures"
const concreteAdmixtureSubcategories = ["Plastisizers", "Accelerators"];

/** Categories the visitor can filter by that don't depend on the CMS. */
const LOCAL_CATEGORIES = LOCAL_PRODUCTS.map((product) => product.category);

const Products: React.FC<ProductsProps> = ({ showDropdown = true }) => {
  const { isLoading, setLoading } = usePageLoading();
  const location = useLocation();
  
  // The Home page preview (showDropdown=false) always shows everything; only the full
  // page remembers a filter (see useRememberedCategory for what is kept and when).
  const [selectedCategory, setSelectedCategory] = useRememberedCategory(showDropdown, "All Products");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [sanityProducts, setSanityProducts] = useState<Product[]>([]);
  const [dropdownItems, setDropdownItems] = useState<string[]>(["All Products"]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const combinedProducts = useMemo(
    () => [...LOCAL_PRODUCTS, ...sanityProducts],
    [sanityProducts]
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement | null>(null);
  const [dropdownHeight, setDropdownHeight] = useState(0);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Helper function to check if category is Concrete Admixtures (defined early for use in filteredDropdownItems)
  const isConcreteAdmixtures = (category: string) => 
    category.toLowerCase() === "concrete admixtures";

// Filter out selected category from dropdown list to avoid duplication
// Submenu for Concrete Admixtures will be attached to the button itself
const filteredDropdownItems = dropdownItems.filter((item) => {
  return item !== selectedCategory;
});

useEffect(() => {
  if (dropdownContentRef.current) {
    setDropdownHeight(dropdownContentRef.current.offsetHeight);
  }
}, [dropdownOpen, filteredDropdownItems.length]);


  // Local products render immediately; CMS products and categories are added as they
  // arrive. The two requests are independent, so run them in parallel and let either
  // one fail without discarding the other.
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const loadSanityContent = async () => {
      const [productsResult, categoriesResult] = await Promise.allSettled([
        fetchProductCards({ signal }),
        fetchProductCategories({ signal }),
      ]);

      if (signal.aborted) return;

      if (productsResult.status === "fulfilled") {
        setSanityProducts(
          productsResult.value.map((product) => ({
            ...product,
            cardContent: product.cardContent ?? "",
            category: product.category ?? "",
          }))
        );
      } else {
        console.error("Failed to fetch Sanity products:", productsResult.reason);
      }

      if (categoriesResult.status === "fulfilled") {
        setDropdownItems(["All Products", ...categoriesResult.value]);
        setCategoriesLoaded(true);
      } else {
        console.error("Failed to fetch Sanity categories:", categoriesResult.reason);
      }
    };

    loadSanityContent();

    return () => controller.abort();
  }, []);

  // A remembered category can disappear (e.g. renamed in the CMS). Once the real category
  // list is known, fall back to "All Products" instead of showing an empty page.
  useEffect(() => {
    if (!categoriesLoaded || selectedCategory === "All Products") return;

    const known = new Set(
      [...dropdownItems, ...LOCAL_CATEGORIES, ...concreteAdmixtureSubcategories].map((name) =>
        name.toLowerCase()
      )
    );
    if (!known.has(selectedCategory.toLowerCase())) setSelectedCategory("All Products");
  }, [categoriesLoaded, selectedCategory, dropdownItems, setSelectedCategory]);

  // Keep submenu visible when Concrete Admixtures is selected
  useEffect(() => {
    if (isConcreteAdmixtures(selectedCategory)) {
      // Find the Concrete Admixtures item in dropdownItems and set it as hovered
      const concreteAdmixturesItem = dropdownItems.find(item => isConcreteAdmixtures(item));
      if (concreteAdmixturesItem) {
        setHoveredCategory(concreteAdmixturesItem);
      }
    }
  }, [selectedCategory, dropdownItems]);

  const handleProductNavigation = (targetPath: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === targetPath) {
      e.preventDefault();
      setHoveredCategory(null);
      return;
    }
    setLoading(true);
    setHoveredCategory(null);
    setDropdownOpen(false);
  };

  // Map products to subcategories - use subcategory from product data if available, otherwise infer from name
  const getProductSubcategory = (product: Product): string => {
    // If product has subcategory defined, use it
    if (product.subcategory) {
      return product.subcategory;
    }
    
    // Fallback: infer from product name
    const title = product.cardTitle.toLowerCase();
    if (title.includes("superplast")) {
      return "Plastisizers";
    } else if (title.includes("no. 3") || title.includes("afa") || title.includes("accelerat")) {
      return "Accelerators";
    } else if (title.includes("crack") || title.includes("fiber")) {
      return "Accelerators";
    }
    return "Plastisizers"; // default
  };

  // Filter products for Concrete Admixtures
  const concreteAdmixtureProducts = combinedProducts.filter(
    (p) => p.category && p.category.toLowerCase() === "concrete admixtures"
  );

  // Get products by subcategory
  const getProductsBySubcategory = (subcategory: string) => {
    return concreteAdmixtureProducts.filter(
      (p) => getProductSubcategory(p) === subcategory
    );
  };

  // Check if selectedCategory is a subcategory
  const isSubcategorySelected = concreteAdmixtureSubcategories.some(
    (sub) => sub.toLowerCase() === selectedCategory.toLowerCase()
  );

  // Filter products based on selected category or subcategory
  let filteredProducts;
  if (selectedCategory === "All Products") {
    filteredProducts = combinedProducts;
  } else if (isConcreteAdmixtures(selectedCategory)) {
    // If "Concrete Admixtures" category is selected, show all concrete admixture products
    // Check this BEFORE isSubcategorySelected to avoid conflicts
    filteredProducts = concreteAdmixtureProducts;
  } else if (isSubcategorySelected) {
    // If a subcategory is selected, show products from that subcategory
    filteredProducts = getProductsBySubcategory(selectedCategory);
  } else {
    // Regular category filtering
    filteredProducts = combinedProducts.filter(
      (p) => p.category && p.category.toLowerCase() === selectedCategory.toLowerCase()
    );
  }

  if (!showDropdown) {
    filteredProducts = filteredProducts.slice(0, 6);
  }

  return (
    <div className="2xl:px-40 xl:px-35 lg:px-30 md:px-20 px-3 my-[5rem]">
      <div className="mx-auto">
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={!isLoading ? { opacity: 1, scaleX: 1 } : { opacity: 0, scaleX: 0 }}
          transition={{ duration: 0.5 }}
        >
          <TitleWithContent title="Products" level={showDropdown ? 1 : 2} />
        </motion.div>

        {showDropdown && (
          <div
            className="relative w-full max-w-[320px] mt-6 mx-auto 2xl:ml-[7rem] xl:ml-[5rem] lg:ml-[4rem]"
            ref={dropdownRef}
            onMouseLeave={(e) => {
              // Only clear hover if mouse is leaving the entire container area
              // Check if we're moving to the submenu or arrow trigger
              const relatedTarget = e.relatedTarget as HTMLElement;
              if (relatedTarget) {
                // Check if moving to submenu or arrow trigger
                const isMovingToSubmenu = relatedTarget.closest('.submenu-container');
                const isMovingToArrow = relatedTarget.closest('.subcategory-arrow-trigger');
                if (!isMovingToSubmenu && !isMovingToArrow && !dropdownRef.current?.contains(relatedTarget)) {
                  if (isConcreteAdmixtures(selectedCategory)) {
                    setHoveredCategory(null);
                  }
                }
              } else {
                // No related target means mouse left the window
                if (isConcreteAdmixtures(selectedCategory)) {
                  setHoveredCategory(null);
                }
              }
            }}
          >
            <ButtonElement
              btnText={
                <div className="flex justify-between items-center w-full pr-3">
                  <div className="flex items-center gap-2">
                    <motion.svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      animate={{ rotate: dropdownOpen ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-white mr-2"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </motion.svg>
                    <span>{selectedCategory}</span>
                  </div>
                  {isConcreteAdmixtures(selectedCategory) && (
                    <div
                      className="subcategory-arrow-trigger px-3 py-2 -mx-3 -my-2 cursor-pointer"
                      onMouseEnter={() => {
                        if (isConcreteAdmixtures(selectedCategory)) {
                          setHoveredCategory(selectedCategory);
                        }
                      }}
                      onMouseLeave={(e) => {
                        // Only clear if not moving to submenu
                        const relatedTarget = e.relatedTarget as HTMLElement;
                        if (relatedTarget && !relatedTarget.closest('.submenu-container')) {
                          // Small delay to allow movement to submenu
                          setTimeout(() => {
                            if (!dropdownRef.current?.querySelector('.submenu-container:hover')) {
                              setHoveredCategory(null);
                            }
                          }, 150);
                        }
                      }}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              }
              onClick={() => setDropdownOpen((open) => !open)}
              size="small"
              className="w-full text-left rounded-sm"
            />
            
            {/* Submenu attached to button when Concrete Admixtures is selected */}
            {isConcreteAdmixtures(selectedCategory) && (
              <AnimatePresence>
                {hoveredCategory === selectedCategory && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-0 left-full ml-2 w-64 bg-white border border-gray-300 rounded-sm shadow-lg z-[60] flex flex-col submenu-container"
                    onMouseEnter={() => {
                      if (isConcreteAdmixtures(selectedCategory)) {
                        setHoveredCategory(selectedCategory);
                      }
                    }}
                    onMouseLeave={(e) => {
                      // Only clear if moving outside both arrow trigger and submenu
                      const relatedTarget = e.relatedTarget as HTMLElement;
                      if (relatedTarget) {
                        const isMovingToArrow = relatedTarget.closest('.subcategory-arrow-trigger');
                        if (!isMovingToArrow && !dropdownRef.current?.contains(relatedTarget)) {
                          setHoveredCategory(null);
                        }
                      } else {
                        // No related target means mouse left the window
                        setHoveredCategory(null);
                      }
                    }}
                  >
                    {concreteAdmixtureSubcategories.map((subcategory) => {
                      return (
                        <motion.div
                          key={subcategory}
                          whileHover={{
                            scale: 1.00025,
                            marginTop: 1.5,
                            marginBottom: 1.5,
                            boxShadow: "0px 5px 10px rgba(0, 0, 0, 0.15)",
                            transition: {
                              type: "spring",
                              stiffness: 300,
                              damping: 20,
                              duration: 0.4,
                            },
                          }}
                          initial={{ marginTop: 0, marginBottom: 0 }}
                          animate={{ marginTop: 0, marginBottom: 0 }}
                          className="px-0.5"
                        >
                          <ButtonElement
                            btnText={subcategory}
                            onClick={() => {
                              setSelectedCategory(subcategory);
                              setDropdownOpen(false);
                              setHoveredCategory(null);
                            }}
                            size="small"
                            className={`w-full text-left rounded-sm border-0 bg-custom-secolor text-black hover:bg-custom-secolor hover:text-white relative z-20 ${
                              selectedCategory === subcategory ? 'bg-custom-secolor text-white' : ''
                            }`}
                          />
                        </motion.div>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            )}
            <AnimatePresence>
              {dropdownOpen && (
                <motion.div
                  key="dropdown"
                  ref={dropdownContentRef}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{
                    duration: 0.3,
                    ease: "easeOut",
                    delay: 0.45,
                  }}
                  className="absolute top-full left-0 right-0 bg-white border border-gray-300 rounded-sm mt-1 shadow-lg z-20 flex flex-col"
                >
                  {filteredDropdownItems.map((item) => (
                    <motion.div
                      key={item}
                      whileHover={
                        isConcreteAdmixtures(item)
                          ? {}
                          : {
                              scale: 1.05,
                              marginTop: 3,
                              marginBottom: 3,
                              boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.15)",
                              transition: {
                                type: "spring",
                                stiffness: 300,
                                damping: 20,
                                duration: 0.4,
                              },
                            }
                      }
                      initial={{ marginTop: 0, marginBottom: 0 }}
                      animate={{ marginTop: 0, marginBottom: 0 }}
                      className="px-0.5 relative"
                      onMouseEnter={() => {
                        if (isConcreteAdmixtures(item)) {
                          setHoveredCategory(item);
                        }
                      }}
                      onMouseLeave={() => {
                        // Only clear hover if Concrete Admixtures is not selected
                        // This keeps the submenu visible when the category is selected
                        if (!isConcreteAdmixtures(selectedCategory)) {
                          setHoveredCategory(null);
                        } else if (isConcreteAdmixtures(item) && isConcreteAdmixtures(selectedCategory)) {
                          // Keep hover state when Concrete Admixtures is selected
                          setHoveredCategory(item);
                        }
                      }}
                    >
                      <ButtonElement
                        btnText={
                          <div className="flex items-center justify-between w-full">
                            <span>{item}</span>
                            {isConcreteAdmixtures(item) && (
                              <svg
                                className="w-4 h-4 ml-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            )}
                          </div>
                        }
                        onClick={() => {
                          // Don't do anything if clicking on already selected category (except Concrete Admixtures)
                          if (item === selectedCategory && !isConcreteAdmixtures(item)) {
                            setDropdownOpen(false);
                            return;
                          }
                          
                          if (!isConcreteAdmixtures(item)) {
                            setSelectedCategory(item);
                            setDropdownOpen(false);
                          } else {
                            // If clicking on Concrete Admixtures, select it and close dropdown
                            // The submenu will be shown via hover or when dropdown is reopened
                            setSelectedCategory(item);
                            setHoveredCategory(item);
                            setDropdownOpen(false);
                          }
                        }}
                        size="small"
                        className={`w-full text-left rounded-sm border-0 bg-custom-secolor text-black hover:bg-custom-secolor hover:text-white relative z-20 ${
                          item === selectedCategory ? 'opacity-60 cursor-default' : ''
                        }`}
                      />
                      
                      {/* Multilevel submenu for Concrete Admixtures - showing subcategories */}
                      <AnimatePresence>
                        {isConcreteAdmixtures(item) && (
                          hoveredCategory === item || 
                          (isConcreteAdmixtures(selectedCategory) && item.toLowerCase() === selectedCategory.toLowerCase())
                        ) && (
                          <motion.div
                            key={`submenu-${item}`}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute left-full top-0 ml-2 w-64 bg-white border border-gray-300 rounded-sm shadow-lg z-[60] flex flex-col"
                            onMouseEnter={() => {
                              setHoveredCategory(item);
                            }}
                            onMouseLeave={() => {
                              // Only clear hover if Concrete Admixtures is not selected
                              // Keep submenu visible when category is selected
                              if (!isConcreteAdmixtures(selectedCategory)) {
                                setHoveredCategory(null);
                              } else {
                                // Keep hover state when category is selected
                                setHoveredCategory(item);
                              }
                            }}
                          >
                            {concreteAdmixtureSubcategories.map((subcategory) => {
                              return (
                                <motion.div
                                  key={subcategory}
                                  whileHover={{
                                    scale: 1.00025,
                                    marginTop: 1.5,
                                    marginBottom: 1.5,
                                    boxShadow: "0px 5px 10px rgba(0, 0, 0, 0.15)",
                                    transition: {
                                      type: "spring",
                                      stiffness: 300,
                                      damping: 20,
                                      duration: 0.4,
                                    },
                                  }}
                                  initial={{ marginTop: 0, marginBottom: 0 }}
                                  animate={{ marginTop: 0, marginBottom: 0 }}
                                  className="px-0.5"
                                >
                                  <ButtonElement
                                    btnText={subcategory}
                                    onClick={() => {
                                      setSelectedCategory(subcategory);
                                      setDropdownOpen(false);
                                      setHoveredCategory(null);
                                    }}
                                    size="small"
                                    className={`w-full text-left rounded-sm border-0 bg-custom-secolor text-black hover:bg-custom-secolor hover:text-white relative z-20 ${
                                      selectedCategory === subcategory ? 'bg-custom-secolor text-white' : ''
                                    }`}
                                  />
                                </motion.div>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      <div className="2xl:px-40 xl:px-35 lg:px-30 md:px-20 px-5">
        <motion.div
          initial={{ marginTop: 40 }}
          animate={{ marginTop: dropdownOpen ? dropdownHeight + 20 : 40 }}
          transition={{ type: "tween", duration: 0.4, ease: "easeOut" }}
          className="transition-all duration-500 max-w-[1200px] mx-auto"
        >
          <motion.div
            className="grid gap-y-10 gap-x-6 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 place-items-center"
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
          >
            {filteredProducts.map((product, index) => {
              const scale =
                hoveredIndex === null ? 1 : hoveredIndex === index ? 1.015 : 0.97;

              return (
                <div
                  key={product.linkUrl}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  className="w-full max-w-[280px] min-h-[300px] md:max-w-[330px] md:min-h-[380px]"
                  style={{
                    transform: `scale(${scale})`,
                    transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
                    boxShadow:
                      hoveredIndex === index
                        ? "0px 10px 20px rgba(0, 0, 0, 0.15)"
                        : "none",
                  }}
                >
                  <CardWithImage
                    linkUrl={product.linkUrl}
                    imageUrl={product.imageUrl}
                    image={product.image}
                    cardTitle={product.cardTitle}
                    cardContent={product.cardContent}
                  />
                </div>
              );
            })}
          </motion.div>

          {!showDropdown && (
            <div className="text-center mt-6">
                <ButtonElement
                  btnText="See More Products"
                  size="small"
                  overrideTextColor={true}
                  className="bg-transparent text-custom-secolor hover:text-white hover:bg-custom-secolor-dark px-6 py-2 rounded-sm"
                  onClick={() => (window.location.href = "/products")}
                />
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Products;
